import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import IconButton from '../../../shared/IconButton';
import IconLine from '../../../IconLine';
import StateCell from '../../../cells/StateCell';
import { useNavigate } from 'react-router-dom';
import { useLatestFormTemplatesQuery } from '../../../../hooks/queries/useFormTemplatesQuery';
import {
    useCreateFormTemplateMutation,
    useCreateVersionFormTemplateMutation,
    useDeleteFormTemplateMutation,
    useDesactivateFormTemplateMutation,
    useDuplicateFormTemplateMutation,
} from '../../../../hooks/mutations/useFormTemplateMutation';
import { formTemplateStatusEnum, getFormTemplateStatusLabel, permissionLevels } from '../../../../utils/enums';
import { getFormTemplateStatusColor } from '../../../../utils/colors';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import GenericAgGridTable from '../../GenericAgGridTable';
import Icon from '../../../shared/Icon';
import { Button } from '@mui/material';
import { FormTemplate } from '../../../../services/apiSLTP/qhse/forms/formTemplates';
import { useAuth } from '../../../../hooks/useAuth';
import { useBoolean } from '../../../../hooks/useBoolean';
import CreateFormModal from '../../../modals/qhse/forms/CreateFormModal';
import DuplicateFormModal from '../../../modals/qhse/forms/DuplicateFormModal';
import { useResponseByTemplateIdQuery } from '../../../../hooks/queries/qhse/forms/useFormResponsesQuery';
import { FormResponsesWithSiteAndFilledByResponse, FormResponseWithSiteAndFilledBy } from '../../../../services/apiSLTP/qhse/forms/formResponses';
import { formResponseStatusEnum } from '../../../../constants/forms';

interface FormResponsesTableProps {
    templateName?: string;
    templateId: string;
    onSelectResponse: (responseId: string) => void;
    responses?: FormResponseWithSiteAndFilledBy[];
    isResponsesLoading?: boolean;
    showSiteColumn?: boolean;
}

const FormResponsesTable: React.FC<FormResponsesTableProps> = ({
    templateName,
    templateId,
    onSelectResponse,
    responses,
    isResponsesLoading,
    showSiteColumn,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const handleOpen = (form: FormResponseWithSiteAndFilledBy) => {
        onSelectResponse(form._id);
    };

    const columnDefs: ColDef<FormResponseWithSiteAndFilledBy>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            valueGetter: (params) => (params.data ? formatName(params.data.filledBy.firstName, params.data.filledBy.lastName) : ''),
            initialFlex: 1,
            minWidth: 300,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            colId: 'actions',
            sortable: false,
            filter: false,
            initialWidth: 250,
            cellRenderer: (params: ICellRendererParams<FormResponseWithSiteAndFilledBy>) => {
                if (!params.data) return null;
                const form = params.data;
                return (
                    <IconLine>
                        <IconButton iconName="visibility" title={capitalizeFirstLetter(t('open'))} onClick={() => handleOpen(form)} />
                    </IconLine>
                );
            },
        },

        ...((showSiteColumn
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('site')),
                      field: 'siteId.name',
                      colId: 'site',
                      initialWidth: 300,
                  },
              ]
            : []) as ColDef<FormResponseWithSiteAndFilledBy>[]),
        {
            headerName: capitalizeFirstLetter(t('date')),
            field: 'submittedAt',
            valueGetter: (params) => params.data?.submittedAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'submittedAt',
        },

        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<FormResponseWithSiteAndFilledBy>[]),
    ];

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={
                    responses
                        ?.filter((response) => response.status === formResponseStatusEnum.SUBMITTED)
                        .sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || '')) || []
                }
                loading={isResponsesLoading}
                exportCsvFileName={`responses-${templateName}`}
                agGridProps={{ getRowId: ({ data }) => data._id, domLayout: 'autoHeight', rowNumbers: true }}
            />
        </>
    );
};

export default FormResponsesTable;
